import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Clickable } from '@moda/om';
import { useQuery } from '@apollo/client/react';
import { SizeInventoryFragment } from '../../../generated/types';
import { useCountry } from '../../CountrySelector/useCountry';
import { useAddressesQuery } from '../../../hooks/useAddress';
import { useCookies } from '../../../hooks/useCookies';
import { usePageType } from '../../../hooks/usePageType';
import { tc } from '../../../lib/trackingContext';
import { GET_DELIVERY_ESTIMATE_QUERY } from './deliveryEstimate.rest';
import {
  DeliveryOption,
  RANGE_COPY,
  buildDeliveryView,
  formatCharges,
  formatCountdown,
  formatSummaryDate,
  hasOwnDeliveryMessage,
  isCompleteZip
} from './deliveryOptions';
import { DeliveryZipForm } from './DeliveryZipForm';
import { useCountryCodeAndCurrency } from './useCountryCodeAndCurrency';
import { useSessionValue } from './useSessionValue';
import { useTimeLeft } from './useTimeLeft';
import { useZipLookup } from './useZipLookup';

import './ProductDeliveryOptions.scss';

const SESSION_ZIP_KEY = 'delivery-zip';
const SESSION_EXPANDED_KEY = 'delivery-options-expanded';

interface Props {
  variantId: string;
  fulfilmentType: number | null;
  size: SizeInventoryFragment | null;
}

const formatShipsBy = (date: string) =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York'
  });

export const ProductDeliveryOptions: React.FC<Props> = ({ variantId, fulfilmentType, size }) => {
  const { country } = useCountry();
  const { addresses } = useAddressesQuery();
  const {
    cookies: { cloudfrontViewerCountry, cloudfrontViewerPostalCode }
  } = useCookies();
  const pagetype = usePageType();
  const [sessionZipCode, setSessionZipCode] = useSessionValue(SESSION_ZIP_KEY);
  const [expandedValue, setExpandedValue] = useSessionValue(SESSION_EXPANDED_KEY);
  const [editing, setEditing] = useState(false);
  const [previewZipCode, setPreviewZipCode] = useState<string>();
  const [submittedZipCode, setSubmittedZipCode] = useState<string>();
  const changeRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const returnFocus = useRef(false);

  const isUS = country.alpha3Code === 'USA';
  const savedZipCode =
    addresses[0]?.attributes.countryCode === 'US' ? addresses[0].attributes.postalCode : undefined;
  const guessedZipCode =
    cloudfrontViewerCountry === 'US' &&
    cloudfrontViewerPostalCode &&
    isCompleteZip(cloudfrontViewerPostalCode)
      ? cloudfrontViewerPostalCode
      : undefined;
  const committedZipCode = isUS ? sessionZipCode || savedZipCode || guessedZipCode : undefined;
  const zipCode = (editing && previewZipCode) || committedZipCode;

  const lookup = useZipLookup(zipCode);
  const hasLocation = !isUS || (Boolean(zipCode) && lookup.status !== 'not-found');
  const { updatedCountryCode, updatedCurrency } = useCountryCodeAndCurrency(country.name);

  const { data, previousData, loading, error } = useQuery(GET_DELIVERY_ESTIMATE_QUERY, {
    variables: {
      variantId,
      zipCode,
      countryCode: updatedCountryCode,
      currency: updatedCurrency,
      stateCode: lookup.status === 'found' ? lookup.state : undefined
    },
    skip: hasOwnDeliveryMessage(fulfilmentType) || !hasLocation || lookup.status === 'loading'
  });

  const previewing = editing && Boolean(previewZipCode);
  const estimate = (previewing ? data : (data ?? previousData))?.deliveryEstimate;
  const now = new Date();
  const view = buildDeliveryView({ estimate, fulfilmentType, size, hasLocation, now });
  const timeLeft = useTimeLeft(view.kind === 'options' ? view.cutoffAt : null);

  useEffect(() => {
    if (editing || !returnFocus.current) return;
    returnFocus.current = false;
    changeRef.current?.focus();
  }, [editing]);

  const closeForm = useCallback(() => {
    returnFocus.current = true;
    setEditing(false);
    setPreviewZipCode(undefined);
    setSubmittedZipCode(undefined);
  }, []);

  useEffect(() => {
    if (!submittedZipCode || lookup.zip !== submittedZipCode) return;
    if (lookup.status === 'loading') return;
    if (lookup.status === 'found' && !error) {
      if (loading || !data) return;
      tc.track('Delivery Estimated with Zip Code', { addToTrackingEvent: { pagetype } });
      setSessionZipCode(submittedZipCode);
      closeForm();
    } else {
      setSubmittedZipCode(undefined);
    }
  }, [submittedZipCode, lookup, data, loading, error, pagetype, setSessionZipCode, closeForm]);

  if (hasOwnDeliveryMessage(fulfilmentType)) return null;
  if (view.kind === 'hidden' && !editing && !loading && lookup.status !== 'loading') return null;
  if (error && !editing) return null;

  const previewFailed =
    editing &&
    Boolean(previewZipCode) &&
    (lookup.status === 'not-found' || lookup.status === 'error' || Boolean(error));
  const awaitingZipCode = editing && !(previewZipCode && lookup.status === 'found');
  const expanded = expandedValue === 'true';
  const currency = estimate?.currency ?? 'USD';
  const price = (option: DeliveryOption) => formatCharges(option.charges, currency);

  const summary = () => {
    if (view.kind === 'range') return RANGE_COPY;

    if (view.kind === 'size-ships-later') {
      return view.shipsBy
        ? `This size ships by ${formatShipsBy(view.shipsBy)}`
        : 'This size ships later than other sizes';
    }

    if (view.kind !== 'options') return null;

    const [fastest] = view.options;
    const date = (
      <span className="ProductDeliveryOptions__strong">{formatSummaryDate(fastest, now)}</span>
    );
    const method = `${fastest.label} delivery · ${price(fastest)}`;

    if (fastest.sameDay && timeLeft !== null) {
      return (
        <>
          Order within{' '}
          <span className="ProductDeliveryOptions__strong">{formatCountdown(timeLeft)}</span> to get
          it {date} with {method}
        </>
      );
    }

    return (
      <>
        Get it by {date} with {method}
      </>
    );
  };

  const location =
    lookup.status === 'found' ? `${lookup.city}, ${lookup.state} ${lookup.zip}` : zipCode;
  const showsLocationLine = view.kind === 'options' || view.kind === 'needs-zip';
  const canToggle = view.kind === 'options' && view.options.length > 1;

  return (
    <div
      className={classNames('ProductDeliveryOptions', {
        'ProductDeliveryOptions--loading': loading && estimate
      })}
    >
      {view.kind === 'hidden' ? (
        !error && (
          <div className="ProductDeliveryOptions__skeleton" aria-hidden>
            <span className="ProductDeliveryOptions__bar" />
            <span className="ProductDeliveryOptions__bar ProductDeliveryOptions__bar--short" />
          </div>
        )
      ) : (
        <>
          {!previewFailed && view.kind !== 'needs-zip' && (
            <p className="ProductDeliveryOptions__summary">{summary()}</p>
          )}

          {showsLocationLine && (
            <div className="ProductDeliveryOptions__location-line">
              {isUS && (
                <span className="ProductDeliveryOptions__location">
                  {view.kind === 'options' && <span>{location}</span>}
                  <Clickable
                    ref={changeRef}
                    type="button"
                    className="ProductDeliveryOptions__link"
                    onClick={() => setEditing(true)}
                  >
                    {view.kind === 'needs-zip' ? 'Enter ZIP for delivery dates' : 'Change'}
                  </Clickable>
                </span>
              )}

              {canToggle && (
                <Clickable
                  type="button"
                  className="ProductDeliveryOptions__link ProductDeliveryOptions__toggle"
                  aria-expanded={expanded}
                  aria-controls="delivery-options"
                  onClick={() => setExpandedValue(expanded ? undefined : 'true')}
                >
                  {expanded ? 'Fewer delivery dates' : 'More delivery dates'}
                </Clickable>
              )}
            </div>
          )}
        </>
      )}

      {editing && (
        <div className="ProductDeliveryOptions__form">
          <div className="ProductDeliveryOptions__form-inner">
            <DeliveryZipForm
              lookup={lookup}
              serviceError={Boolean(error)}
              onPreview={setPreviewZipCode}
              onSubmit={setSubmittedZipCode}
              onCancel={closeForm}
            />
          </div>
        </div>
      )}

      {view.kind === 'options' && (
        <ul
          id="delivery-options"
          className={classNames('ProductDeliveryOptions__options', {
            'ProductDeliveryOptions__options--dimmed': awaitingZipCode
          })}
          aria-live="polite"
          hidden={!expanded || previewFailed}
          inert={awaitingZipCode}
        >
          {view.options.map(option => (
            <li key={option.id} className="ProductDeliveryOptions__option">
              <span className="ProductDeliveryOptions__arrives">
                Arrives by <span className="ProductDeliveryOptions__date">{option.date}</span>
              </span>
              <span>{option.label}</span>
              <span className="ProductDeliveryOptions__separator" aria-hidden>
                ·
              </span>
              <span className="ProductDeliveryOptions__price">{price(option)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
